/**
 * WorkspaceCard — renders the active station's identity at the top of the
 * sidebar so users always know which station they're producing for.
 *
 * Lives between the BroadcastFlow logo and the nav list. Auto-refreshes on
 * the `station-switched` event (dispatched by StationSwitcher).
 *
 * Collapsed sidebar: shows only the genre-tinted dot + station initial.
 * Expanded sidebar: shows logo (if present) + name + genre badge.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Radio } from "lucide-react";
import { api } from "@/lib/api";
import { getStationTheme } from "@/lib/stationTheme";

export default function WorkspaceCard({ collapsed }) {
  const [station, setStation] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/stations/me");
      setStation(r.data || null);
    } catch {
      setStation(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onSwitch = () => load();
    window.addEventListener("station-switched", onSwitch);
    return () => window.removeEventListener("station-switched", onSwitch);
  }, [load]);

  if (!station) return null;

  const theme = getStationTheme(station.genre);
  const initial = (station.name || "?")[0]?.toUpperCase();

  if (collapsed) {
    return (
      <div
        className="mx-2 my-2 flex h-12 w-12 items-center justify-center rounded-lg border bg-bg-elevated"
        style={theme.borderStyle}
        data-testid="workspace-card-collapsed"
        title={`Workspace: ${station.name}`}
      >
        {station.logo_url ? (
          <img src={station.logo_url} alt="" className="h-8 w-8 rounded object-cover" />
        ) : (
          <span className="font-display text-base font-bold" style={{ color: theme.hex }}>
            {initial}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className="mx-3 my-3 rounded-lg border bg-bg-elevated overflow-hidden"
      style={theme.borderStyle}
      data-testid="workspace-card"
    >
      {/* Top accent band (genre color) */}
      <div className="h-1 w-full" style={theme.barStyle} />
      <div className="p-3">
        <div className="flex items-center gap-2 mb-1.5">
          <Radio className="h-3 w-3" style={{ color: theme.hex }} />
          <span className="text-[9px] font-mono uppercase tracking-[0.18em] text-ink-muted">
            // Active workspace
          </span>
        </div>
        <div className="flex items-center gap-2.5">
          {station.logo_url ? (
            <img
              src={station.logo_url}
              alt=""
              className="h-9 w-9 rounded-md object-cover border"
              style={theme.borderStyle}
            />
          ) : (
            <div
              className="h-9 w-9 rounded-md flex items-center justify-center font-display text-lg font-bold border"
              style={{ ...theme.tintStyle, ...theme.borderStyle, color: theme.hex }}
              data-testid="workspace-card-avatar"
            >
              {initial}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div
              className="type-app-title text-ink-primary truncate"
              data-testid="workspace-card-name"
              title={station.name}
            >
              {station.name}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="h-1.5 w-1.5 rounded-full" style={theme.dotStyle} />
              <span
                className="type-status-badge font-mono uppercase tracking-wider truncate"
                style={{ color: theme.hex }}
                data-testid="workspace-card-genre"
                title={station.genre || theme.label}
              >
                {station.genre || theme.label}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
